/**
 * Processa dados importados, tratando especialmente campos telefônicos.
 *
 * @param {Object} options - Opções para processamento
 * @param {Object} options.data - Dados a serem processados
 * @param {Array<Object>} options.fields - Campos disponíveis com seus tipos
 * @param {Array<Array<string>>} options.fileData - Matriz contendo dados do arquivo importado
 * @param {boolean} options.useBrazilianDDI - Flag que indica se deve usar DDI brasileiro (55) por padrão
 * @param {string} [options.type] - Tipo de importação ('crmEntities' ou 'broadcastList')
 *
 * @returns {Array<Object>} Array de objetos representando os dados processados
 *
 * @description
 * Esta função transforma dados importados em uma estrutura padronizada, fazendo o tratamento
 * especial para campos do tipo telefone, separando-os em partes (DDI e número).
 * O processo inclui:
 * 1. Processamento inicial dos dados de entrada
 * 2. Extração e filtro das colunas relevantes
 * 3. Mapeamento dos cabeçalhos
 * 4. Transformação dos dados em um formato final (DTO)
 */

export const processImportData = ({
  data,
  fields,
  fileData,
  useBrazilianDDI,
  type = 'crmEntities'
}) => {
  const headerRow = fileData[0];
  const dataRows = fileData.slice(1);

  const fieldNameToIndexMap = {};

  Object.entries(data).forEach(([fieldName, value]) => {
    const field = fields.find((f) => f.name === fieldName);

    if (field?.type === 'TELEPHONE') {
      if (useBrazilianDDI) {
        if (value?.number) {
          const columnIndex = headerRow.indexOf(value.number);
          if (columnIndex > -1) {
            fieldNameToIndexMap[`${fieldName}_number`] = columnIndex;
          }
        }
      } else {
        if (value?.ddi) {
          const columnIndex = headerRow.indexOf(value.ddi);
          if (columnIndex > -1) {
            fieldNameToIndexMap[`${fieldName}_ddi`] = columnIndex;
          }
        }
        if (value?.number) {
          const columnIndex = headerRow.indexOf(value.number);
          if (columnIndex > -1) {
            fieldNameToIndexMap[`${fieldName}_number`] = columnIndex;
          }
        }
      }
    } else {
      if (typeof value === 'string') {
        const columnIndex = headerRow.indexOf(value);
        if (columnIndex > -1) {
          fieldNameToIndexMap[fieldName] = columnIndex;
        }
      }
    }
  });

  const dataDTO = dataRows
    .map((row) => {
      const item = {};

      if (type === 'broadcastList') {
        item.fieldAssociation = {};
        item.fieldImport = {};
      }

      fields.forEach((field) => {
        const fieldName = field.name;

        if (field.type === 'TELEPHONE') {
          const numberIndex = fieldNameToIndexMap[`${fieldName}_number`];
          const numberValue = numberIndex !== undefined ? row[numberIndex] : '';

          if (numberValue) {
            if (type === 'broadcastList') {
              item[fieldName] = numberValue;
            } else {
              const ddiIndex = fieldNameToIndexMap[`${fieldName}_ddi`];
              const ddiValue = useBrazilianDDI
                ? '55'
                : ddiIndex !== undefined
                ? row[ddiIndex]
                : '';

              item[fieldName] = {
                ddi: ddiValue || '',
                number: numberValue || ''
              };
            }
          }
        } else {
          const columnIndex = fieldNameToIndexMap[fieldName];
          if (columnIndex !== undefined) {
            const value = row[columnIndex];
            if (type === 'broadcastList') {
              if (field.isProtocolParams) {
                item.fieldAssociation[fieldName] = value;
              } else if (field.isTemplateParams) {
                item.fieldImport[fieldName] = value;
              } else {
                if (fieldName === 'contextParams') {
                  try {
                    item[fieldName] = JSON.parse(value);
                  } catch {
                    item[fieldName] = value;
                  }
                } else {
                  item[fieldName] = value;
                }
              }
            } else {
              item[fieldName] = value;
            }
          }
        }
      });
      return item;
    })
    .filter((item) => Object.keys(item).length > 0);

  return dataDTO;
};
